import { useEffect, useRef, useState } from "react";
import "./SoundPlayer.css";
import { getSoundcloudUrl } from "../../lib/soundcloudService";

/**
 * Primeira reprodução desta carga de página: sorteia a faixa inicial, para o
 * site não abrir sempre na mesma música da playlist. `skip(i)` já inicia o
 * play, então só é chamado quando a intenção é tocar. O guard `randomizedRef`
 * garante que isso acontece uma única vez — pause/play e next/prev depois não
 * re-sorteiam. Playlist de 0 ou 1 faixa cai no play normal.
 */
function startPlayback(w, randomizedRef) {
  if (randomizedRef.current) {
    w.play();
    return;
  }
  w.getSounds((sounds) => {
    if (sounds && sounds.length > 1) {
      randomizedRef.current = true;
      w.skip(Math.floor(Math.random() * sounds.length));
    } else {
      w.play();
    }
  });
}

export default function SoundPlayer() {
  const iframeRef = useRef(null);
  const widgetRef = useRef(null);
  // Marca que a TV foi ligada (power-on) — só então o player pode tocar.
  const wantsPlayRef = useRef(false);
  // Cancela os listeners de "retomar no primeiro gesto" de fora do effect.
  const gestureCleanupRef = useRef(null);
  // A faixa inicial já foi sorteada nesta carga de página?
  const randomizedRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [trackTitle, setTrackTitle] = useState("");
  const [soundcloudUrl, setSoundcloudUrl] = useState(null);

  // Carrega a URL configurável (site_config) antes de montar o widget.
  useEffect(() => {
    let cancelled = false;
    getSoundcloudUrl().then((url) => {
      if (!cancelled) setSoundcloudUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // "Ligar a TV" (TvBootIntro) inicia a playlist. O clique que dispara este
  // evento também é o gesto que libera o autoplay no navegador.
  useEffect(() => {
    const onTvOn = () => {
      wantsPlayRef.current = true;
      if (widgetRef.current) startPlayback(widgetRef.current, randomizedRef);
    };
    window.addEventListener("mxd:tv-on", onTvOn);
    return () => window.removeEventListener("mxd:tv-on", onTvOn);
  }, []);

  // Outra parte do site vai tocar áudio/vídeo (ex.: uma gravação nos
  // Destaques). Dois áudios ao mesmo tempo é sempre erro, então a rádio sai
  // de cena — e junto vão os listeners de "retomar no primeiro gesto", que
  // senão fariam o próprio clique no play religar a rádio.
  useEffect(() => {
    const onPauseRadio = () => {
      wantsPlayRef.current = false;
      gestureCleanupRef.current?.();
      gestureCleanupRef.current = null;
      widgetRef.current?.pause();
    };
    window.addEventListener("mxd:pause-radio", onPauseRadio);
    return () => window.removeEventListener("mxd:pause-radio", onPauseRadio);
  }, []);

  useEffect(() => {
    if (!soundcloudUrl) return;

    // Widget novo → o sorteio da faixa inicial pode acontecer de novo.
    randomizedRef.current = false;

    const clearGestureListeners = () => {
      gestureCleanupRef.current?.();
      gestureCleanupRef.current = null;
    };

    const initWidget = () => {
      if (!iframeRef.current || !window.SC) return;
      widgetRef.current = window.SC.Widget(iframeRef.current);
      const w = widgetRef.current;

      const gestureEvents = ["click", "touchstart", "keydown", "scroll"];

      const playOnGesture = () => {
        startPlayback(w, randomizedRef);
        clearGestureListeners();
      };

      w.bind(window.SC.Widget.Events.PLAY, () => {
        setIsPlaying(true);
        // Update title on every track change (next/prev also fires PLAY)
        w.getCurrentSound((s) => s && s.title && setTrackTitle(s.title));
        clearGestureListeners();
      });
      w.bind(window.SC.Widget.Events.PAUSE, () => setIsPlaying(false));
      w.bind(window.SC.Widget.Events.FINISH, () => setIsPlaying(false));
      w.bind(window.SC.Widget.Events.READY, () => {
        w.getCurrentSound((s) => s && s.title && setTrackTitle(s.title));

        // A TV já foi ligada nesta sessão? (intro pulada num reload)
        const tvAlreadyOn = (() => {
          try {
            return sessionStorage.getItem("mxd_tv_on") === "1";
          } catch {
            return false;
          }
        })();

        if (wantsPlayRef.current) {
          // Power-on aconteceu antes do widget ficar pronto: toca agora.
          startPlayback(w, randomizedRef);
        } else if (tvAlreadyOn) {
          // Sessão já "ligada" mas sem gesto recente (reload): o navegador
          // bloqueia autoplay, então retomamos no primeiro gesto do usuário.
          gestureEvents.forEach((ev) =>
            document.addEventListener(ev, playOnGesture, { passive: true })
          );
          gestureCleanupRef.current = () =>
            gestureEvents.forEach((ev) =>
              document.removeEventListener(ev, playOnGesture)
            );
        }
        // Caso contrário (intro ativa, TV desligada): silêncio até o power-on.
      });
    };

    if (window.SC) {
      initWidget();
      return clearGestureListeners;
    }

    const script = document.createElement("script");
    script.src = "https://w.soundcloud.com/player/api.js";
    script.async = true;
    script.onload = initWidget;
    document.body.appendChild(script);

    return () => {
      clearGestureListeners();
      try { document.body.removeChild(script); } catch (e) {}
    };
  }, [soundcloudUrl]);

  const toggle = () => {
    const w = widgetRef.current;
    if (!w) return;
    isPlaying ? w.pause() : w.play();
  };

  const prev = () => widgetRef.current?.prev();
  const next = () => widgetRef.current?.next();

  return (
    <>
      {/* Ghost player — desktop only (>880px) */}
      <div className={`player-ghost${isPlaying ? " playing" : ""}`}>
        <button className="ghost-btn" onClick={prev} aria-label="previous">‹</button>
        <button className={`ghost-btn play-btn`} onClick={toggle} aria-label={isPlaying ? "pause" : "play"}>
          {isPlaying ? "❚❚" : "▶"}
        </button>
        <button className="ghost-btn" onClick={next} aria-label="next">›</button>
        <span className="ghost-title">{trackTitle || "mixordia"}</span>
        <div className="wave small">
          {[...Array(8)].map((_, i) => <span key={i} />)}
        </div>
      </div>

      {/* Floating pill — mobile only (≤880px), always visible */}
      <div className={`player-pill${isPlaying ? " playing" : ""}`}>
        <div className="pill-controls">
          <button className="ghost-btn" onClick={prev} aria-label="previous">‹</button>
          <button className="ghost-btn play-btn" onClick={toggle} aria-label={isPlaying ? "pause" : "play"}>
            {isPlaying ? "❚❚" : "▶"}
          </button>
          <button className="ghost-btn" onClick={next} aria-label="next">›</button>
          <div className="wave small">
            {[...Array(6)].map((_, i) => <span key={i} />)}
          </div>
        </div>
        <span className="pill-title">{trackTitle || "mixordia"}</span>
      </div>

      {/* Hidden iframe — SC Widget API target */}
      {soundcloudUrl && (
        <iframe
          ref={iframeRef}
          id="sc-widget"
          title="SoundCloud"
          allow="autoplay; encrypted-media"
          className="hidden-soundcloud"
          src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(soundcloudUrl)}&hide_related=true&visual=false&auto_play=false`}
        />
      )}
    </>
  );
}
